import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RichTextEditor } from '../components/ui/rich-text-editor';
import { Newspaper, Megaphone, FileText, BarChart3, Plus, Edit, Trash2, User, Calendar, Check, X, MessageCircle, Send, Eye, Link } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { newsAPI, trackingAPI } from '../services/api';
import { sanitizeHtml } from '../utils/sanitize';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const News = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [comments, setComments] = useState({}); // {news_id: [comments]}
  const [newComment, setNewComment] = useState({}); // {news_id: "comment text"}
  const [expandedComments, setExpandedComments] = useState({}); // {news_id: true/false}
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    news_type: 'news',
    poll_options: '',
    image_url: ''
  });
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      setLoading(true);
      const data = await newsAPI.getAll();
      setNewsList(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des actualités');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (newsId) => {
    try {
      const data = await newsAPI.getComments(newsId);
      setComments(prev => ({ ...prev, [newsId]: data }));
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const toggleComments = async (newsId) => {
    const isExpanded = expandedComments[newsId];
    setExpandedComments(prev => ({ ...prev, [newsId]: !isExpanded }));
    
    if (!isExpanded) {
      // Track article view when expanding
      trackingAPI.articleView(newsId);
      
      if (!comments[newsId]) {
        await loadComments(newsId);
      }
    }
  };

  const handleAddComment = async (newsId) => {
    const content = newComment[newsId]?.trim();
    if (!content) return;

    try {
      await newsAPI.addComment(newsId, content);
      setNewComment(prev => ({ ...prev, [newsId]: '' }));
      await loadComments(newsId);
      toast.success('Commentaire ajouté !');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleDeleteComment = async (commentId, newsId) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    
    try {
      await newsAPI.deleteComment(commentId);
      await loadComments(newsId);
      toast.success('Commentaire supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const handleCreateNews = async (e) => {
    e.preventDefault();
    try {
      const newsData = {
        title: formData.title,
        content: formData.content,
        news_type: formData.news_type,
        poll_options: formData.news_type === 'poll' ? formData.poll_options.split('\n').filter(o => o.trim()) : null,
        image_url: formData.image_url || null
      };
      
      await newsAPI.create(newsData);
      toast.success('Actualité créée !');
      setIsDialogOpen(false);
      resetForm();
      loadNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateNews = async (e) => {
    e.preventDefault();
    try {
      await newsAPI.update(editingNews, {
        title: formData.title,
        content: formData.content,
        image_url: formData.image_url || null
      });
      toast.success('Actualité mise à jour !');
      setIsDialogOpen(false);
      setEditingNews(null);
      resetForm();
      loadNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette actualité ?')) return;
    
    try {
      await newsAPI.delete(newsId);
      toast.success('Actualité supprimée');
      loadNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleVote = async (newsId, option) => {
    try {
      await newsAPI.vote(newsId, option);
      toast.success('Vote enregistré !');
      loadNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du vote');
    }
  };

  const openEditDialog = (news) => {
    setEditingNews(news.news_id);
    setFormData({
      title: news.title,
      content: news.content,
      news_type: news.news_type,
      poll_options: '',
      image_url: news.image_url || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      news_type: 'news',
      poll_options: '',
      image_url: ''
    });
    setEditingNews(null);
  };

  const getNewsIcon = (type) => {
    switch (type) {
      case 'announcement': return <Megaphone className="h-5 w-5" />;
      case 'article': return <FileText className="h-5 w-5" />;
      case 'poll': return <BarChart3 className="h-5 w-5" />;
      default: return <Newspaper className="h-5 w-5" />;
    }
  };

  const getNewsTypeLabel = (type) => {
    switch (type) {
      case 'announcement': return 'Annonce';
      case 'article': return 'Article';
      case 'poll': return 'Sondage';
      default: return 'News';
    }
  };

  const getNewsTypeBadgeColor = (type) => {
    switch (type) {
      case 'announcement': return 'bg-red-600 text-white';
      case 'article': return 'bg-blue-600 text-white';
      case 'poll': return 'bg-green-600 text-white';
      default: return 'bg-purple-600 text-white';
    }
  };

  const getTotalVotes = (pollVotes) => {
    if (!pollVotes) return 0;
    return Object.values(pollVotes).reduce((sum, votes) => sum + votes.length, 0);
  };

  const hasUserVoted = (pollVotes, option) => {
    if (!pollVotes || !pollVotes[option]) return false;
    return pollVotes[option].includes(user?.user_id);
  };

  const getVotePercentage = (pollVotes, option) => {
    const total = getTotalVotes(pollVotes);
    if (total === 0) return 0;
    return Math.round((pollVotes[option]?.length || 0) / total * 100);
  };

  const getImageUrl = (url) => url || '';

  const getAvatarUrl = (avatar) => {
    // Handle base64 data URLs directly
    if (avatar && avatar.startsWith('data:')) {
      return avatar;
    }
    return avatar || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 pb-12 flex items-center justify-center">
        <div className="text-purple-400 text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 pb-12">
      <SEO title="Actualités" description="Actualités, annonces et sondages de l'association de jeu de rôle Just Rôle It." path="/news" />
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-12 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2">
              Actualités
            </h1>
            <p className="text-gray-400">Les dernières nouvelles de l'association</p>
          </div>

          {isAdmin() && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="new-news-btn"
                  className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Nouvelle actualité
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gradient-to-br from-purple-900/95 to-black border-2 border-purple-500/50 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-purple-300">
                    {editingNews ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={editingNews ? handleUpdateNews : handleCreateNews} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-purple-300">Titre</Label>
                    <Input
                      data-testid="news-title-input"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-black/50 border-purple-500/30 text-white"
                      required
                    />
                  </div>

                  {!editingNews && (
                    <div className="space-y-2">
                      <Label className="text-purple-300">Type</Label>
                      <Select value={formData.news_type} onValueChange={(value) => setFormData({ ...formData, news_type: value })}>
                        <SelectTrigger data-testid="news-type-select" className="bg-black/50 border-purple-500/30 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-black border-purple-500/30">
                          <SelectItem value="news" className="text-purple-300">News</SelectItem>
                          <SelectItem value="announcement" className="text-red-400">Annonce</SelectItem>
                          <SelectItem value="article" className="text-blue-400">Article</SelectItem>
                          <SelectItem value="poll" className="text-green-400">Sondage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Image URL */}
                  <div className="space-y-2">
                    <Label className="text-purple-300">URL de l'image (optionnel)</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                        <Input
                          value={formData.image_url}
                          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                          className="bg-black/50 border-purple-500/30 text-white pl-10"
                          placeholder="https://exemple.com/image.jpg"
                        />
                      </div>
                      {formData.image_url && (
                        <Button
                          type="button"
                          onClick={handleRemoveImage}
                          variant="destructive"
                          size="icon"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {formData.image_url && (
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="max-w-full max-h-32 rounded-lg border border-purple-500/30 mt-2"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <p className="text-gray-500 text-xs">Collez l'URL d'une image hébergée en ligne</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-purple-300">Contenu</Label>
                    <RichTextEditor
                      value={formData.content}
                      onChange={(value) => setFormData({ ...formData, content: value })}
                      placeholder="Écrivez votre actualité..."
                    />
                  </div>

                  {!editingNews && formData.news_type === 'poll' && (
                    <div className="space-y-2">
                      <Label className="text-purple-300">Options du sondage (une par ligne)</Label>
                      <Textarea
                        data-testid="poll-options-input"
                        value={formData.poll_options}
                        onChange={(e) => setFormData({ ...formData, poll_options: e.target.value })}
                        placeholder="Option 1&#10;Option 2&#10;Option 3"
                        className="bg-black/50 border-purple-500/30 text-white min-h-[100px]"
                        required
                      />
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    data-testid="news-submit-btn"
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button"
                  >
                    {editingNews ? 'Modifier' : 'Publier'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* News List */}
        {newsList.length === 0 ? (
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-12 text-center">
            <Newspaper className="h-16 w-16 text-purple-400/50 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-purple-300 mb-2">Aucune actualité</h3>
            <p className="text-gray-400">Les actualités de l'association apparaîtront ici</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {newsList.map((news) => (
              <Card 
                key={news.news_id} 
                data-testid={`news-card-${news.news_id}`}
                className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 overflow-hidden hover:border-purple-400/50 transition-all"
              >
                {/* News Image */}
                {news.image_url && (
                  <div className="w-full h-64 overflow-hidden">
                    <img 
                      src={getImageUrl(news.image_url)} 
                      alt={news.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${getNewsTypeBadgeColor(news.news_type)}`}>
                        {getNewsIcon(news.news_type)}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-purple-300">{news.title}</h3>
                        <div className="flex items-center space-x-3 text-sm text-gray-400 mt-1">
                          <span className="flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            {news.author_name}
                          </span>
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(news.created_at).toLocaleDateString('fr-FR')}
                          </span>
                          <Badge className={getNewsTypeBadgeColor(news.news_type)}>
                            {getNewsTypeLabel(news.news_type)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    {isAdmin() && (
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => openEditDialog(news)}
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-news-${news.news_id}`}
                          className="text-purple-400 hover:text-purple-300 hover:bg-purple-900/30"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteNews(news.news_id)}
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-news-${news.news_id}`}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Rich text content */}
                  <div 
                    className="text-gray-300 news-content"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(news.content) }}
                  />

                  {/* Poll Options */}
                  {news.news_type === 'poll' && news.poll_options && (
                    <div className="space-y-3 mt-4 pt-4 border-t border-purple-500/30">
                      <p className="text-sm text-gray-400 mb-2">
                        {getTotalVotes(news.poll_votes)} vote(s)
                      </p>
                      {news.poll_options.map((option, index) => {
                        const percentage = getVotePercentage(news.poll_votes, option);
                        const hasVoted = hasUserVoted(news.poll_votes, option);
                        
                        return (
                          <button
                            key={index}
                            onClick={() => handleVote(news.news_id, option)}
                            className="w-full text-left"
                            data-testid={`poll-option-${news.news_id}-${index}`}
                          >
                            <div className={`relative p-3 rounded-lg border-2 transition-all ${
                              hasVoted 
                                ? 'border-purple-500 bg-purple-900/30' 
                                : 'border-purple-500/30 hover:border-purple-400/50'
                            }`}>
                              <div 
                                className="absolute inset-0 bg-purple-600/20 rounded-lg transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                              <div className="relative flex items-center justify-between">
                                <span className="text-purple-200 flex items-center">
                                  {hasVoted && <Check className="h-4 w-4 mr-2 text-purple-400" />}
                                  {option}
                                </span>
                                <span className="text-purple-400 font-bold">{percentage}%</span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Comments Section */}
                  <div className="mt-4 pt-4 border-t border-purple-500/30">
                    <button
                      onClick={() => toggleComments(news.news_id)}
                      className="flex items-center text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      <span>
                        {expandedComments[news.news_id] ? 'Masquer' : 'Afficher'} les commentaires
                        {comments[news.news_id]?.length > 0 && ` (${comments[news.news_id].length})`}
                      </span>
                    </button>

                    {expandedComments[news.news_id] && (
                      <div className="mt-4 space-y-4">
                        {/* Comment Input */}
                        <div className="flex gap-2">
                          <Input
                            value={newComment[news.news_id] || ''}
                            onChange={(e) => setNewComment(prev => ({ ...prev, [news.news_id]: e.target.value }))}
                            placeholder="Ajouter un commentaire..."
                            className="bg-black/50 border-purple-500/30 text-white flex-1"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddComment(news.news_id)}
                          />
                          <Button
                            onClick={() => handleAddComment(news.news_id)}
                            className="bg-purple-600 hover:bg-purple-500"
                            disabled={!newComment[news.news_id]?.trim()}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Comments List */}
                        {comments[news.news_id]?.length > 0 ? (
                          <div className="space-y-3">
                            {comments[news.news_id].map((comment) => (
                              <div 
                                key={comment.comment_id}
                                className="flex gap-3 p-3 bg-black/30 rounded-lg border border-purple-500/20"
                              >
                                {/* Avatar */}
                                {comment.author_avatar ? (
                                  <img 
                                    src={getAvatarUrl(comment.author_avatar)}
                                    alt={comment.author_name}
                                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                                    {comment.author_name?.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="text-purple-300 font-semibold text-sm">
                                      {comment.author_name}
                                    </span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-500 text-xs">
                                        {new Date(comment.created_at).toLocaleDateString('fr-FR', {
                                          day: 'numeric',
                                          month: 'short',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                      {(comment.author_id === user?.user_id || isAdmin()) && (
                                        <button
                                          onClick={() => handleDeleteComment(comment.comment_id, news.news_id)}
                                          className="text-red-400/50 hover:text-red-400 transition-colors"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <p className="text-gray-300 text-sm mt-1">{comment.content}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm text-center py-2">
                            Aucun commentaire. Soyez le premier à commenter !
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Custom styles for news content */}
      <style>{`
        .news-content h1, .news-content h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          color: #c4b5fd;
        }
        .news-content h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          color: #c4b5fd;
        }
        .news-content p {
          margin-bottom: 0.75rem;
        }
        .news-content ul {
          list-style-type: disc;
          margin-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .news-content ol {
          list-style-type: decimal;
          margin-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .news-content a {
          color: #a855f7;
          text-decoration: underline;
        }
        .news-content b, .news-content strong {
          font-weight: bold;
        }
        .news-content i, .news-content em {
          font-style: italic;
        }
        .news-content u {
          text-decoration: underline;
        }
        .news-content strike, .news-content s {
          text-decoration: line-through;
        }
      `}</style>
    </div>
  );
};

export default News;
